import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import { useLupaRiskReportParams } from '../hooks/reportParams/useLupaRiskReportParams';
import {
  exportLupaRiskReportReportsLupaRiskExportGet,
  getLupaRiskOptionsReportsLupaRiskClinicOptionsGet,
  getLupaRiskReportReportsLupaRiskGet,
} from '../services/api/sdk.gen';
import type {
  LupaRiskReportResult,
  LupaRiskReportRow,
  LupaRiskSortBy,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { parseISOToLocalDate } from '../utils/dateUtils';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { useReportsBackHref } from '../reporting/backHref';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useStore } from '../store';

const ALL_CLINICS = '__all_clinics__';

const statusLabel = (value: string | null) =>
  value === 'lupa'
    ? 'LUPA'
    : value === 'at_risk'
      ? 'At risk'
      : value === 'clear'
        ? 'Clear'
        : 'Unavailable';
const displayValue = (value: unknown) =>
  value === null || value === undefined ? 'Unavailable' : String(value);
const lupaThresholdLabel = (value: unknown, status: string | null) =>
  value === null || value === undefined
    ? status === 'unknown'
      ? 'Unknown (No HIPPS)'
      : 'Unavailable'
    : String(value);

const logApiFailure = (message: string, result: unknown) => {
  console.error(message, { status: getHttpStatus(result) });
};

const normalizeReportError = (result: unknown): unknown => {
  if (result && typeof result === 'object' && 'error' in result) {
    const nested = (result as { error?: unknown }).error;
    if (
      nested &&
      typeof nested === 'object' &&
      'response' in nested &&
      (nested as { response?: { data?: unknown } }).response?.data !== undefined
    ) {
      return nested;
    }
  }
  return result;
};

const columns: ReportTableColumn<LupaRiskReportRow>[] = [
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: (row: LupaRiskReportRow) => row.clinic_name ?? 'No clinic',
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'patient_first_name',
    header: 'Patient first name',
    accessor: (row: LupaRiskReportRow) => displayValue(row.patient_first_name),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'patient_last_name',
    header: 'Patient last name',
    accessor: (row: LupaRiskReportRow) => displayValue(row.patient_last_name),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: (row: LupaRiskReportRow) => displayValue(row.mrn),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'episode_start_date',
    header: 'Episode start',
    accessor: 'episode_start_date' as const,
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'episode_end_date',
    header: 'Episode end',
    accessor: 'episode_end_date' as const,
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'period_1_status',
    header: 'Period 1 status',
    accessor: (row: LupaRiskReportRow) => statusLabel(row.period_1_status),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'period_1_lupa_threshold',
    header: 'Period 1 LUPA threshold',
    accessor: (row: LupaRiskReportRow) =>
      lupaThresholdLabel(row.period_1_lupa_threshold, row.period_1_status),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'period_1_billable_visit_count',
    header: 'Period 1 scheduled visits',
    accessor: (row: LupaRiskReportRow) =>
      displayValue(row.period_1_billable_visit_count),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'period_1_difference',
    header: 'Period 1 difference',
    accessor: (row: LupaRiskReportRow) => displayValue(row.period_1_difference),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'period_2_status',
    header: 'Period 2 status',
    accessor: (row: LupaRiskReportRow) => statusLabel(row.period_2_status),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'period_2_lupa_threshold',
    header: 'Period 2 LUPA threshold',
    accessor: (row: LupaRiskReportRow) =>
      lupaThresholdLabel(row.period_2_lupa_threshold, row.period_2_status),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'period_2_billable_visit_count',
    header: 'Period 2 scheduled visits',
    accessor: (row: LupaRiskReportRow) =>
      displayValue(row.period_2_billable_visit_count),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'period_2_difference',
    header: 'Period 2 difference',
    accessor: (row: LupaRiskReportRow) => displayValue(row.period_2_difference),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'primary_difference',
    header: 'Primary difference',
    accessor: (row: LupaRiskReportRow) => displayValue(row.primary_difference),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'risk_status',
    header: 'Risk/status',
    accessor: (row: LupaRiskReportRow) => statusLabel(row.risk_status),
    enableSorting: true,
    minWidth: 130,
  },
];

const LupaRiskReport = () => {
  const hasPermission = useStore(state => state.hasPermission);
  const reportsBackHref = useReportsBackHref();
  const {
    params,
    searchInput,
    setSearchInput,
    setSearchQuery,
    isSearchPending,
    setAsOfDate,
    persistResolvedAsOfDate,
    setClinicId,
    setSorting,
    setPagination,
  } = useLupaRiskReportParams();
  const [report, setReport] = useState<LupaRiskReportResult | null>(null);
  const [clinics, setClinics] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [error, setError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    asOfDate,
    clinicId,
    query: q,
    page,
    pageSize,
    sortBy,
    sortDirection,
  } = params;
  const displayError = error ?? optionsError;

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    const requestedAsOfDate = asOfDate;
    setLoading(true);
    getLupaRiskReportReportsLupaRiskGet({
      query: {
        as_of_date: asOfDate || null,
        clinic_id: clinicId || null,
        q: q || null,
        sort_by: sortBy,
        sort_direction: sortDirection,
        page,
        page_size: pageSize,
      },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          logApiFailure('Failed to load LUPA Risk Report', response);
          setReport(null);
          setError(
            getBusinessErrorMessage(normalizeReportError(response)) ??
              'Unable to load LUPA Risk Report. Please try again.'
          );
        } else {
          setReport(response.data);
          setError(null);
          if (!requestedAsOfDate) {
            persistResolvedAsOfDate(response.data.as_of_date);
          }
        }
      })
      .catch(caught => {
        if (!cancelled) {
          logApiFailure('Failed to load LUPA Risk Report', caught);
          setReport(null);
          setError(
            getBusinessErrorMessage(caught) ??
              'Unable to load LUPA Risk Report. Please try again.'
          );
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [
    asOfDate,
    clinicId,
    q,
    sortBy,
    sortDirection,
    page,
    pageSize,
    hasPermission,
    persistResolvedAsOfDate,
  ]);

  const pagination: ReportTablePaginationState = useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );
  const sorting: ReportTableSort = { id: sortBy, direction: sortDirection };

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getLupaRiskOptionsReportsLupaRiskClinicOptionsGet({
      query: { as_of_date: asOfDate || null },
    })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          logApiFailure('Failed to load LUPA Risk Report clinics', response);
          setClinics([]);
          setOptionsError(
            getBusinessErrorMessage(normalizeReportError(response)) ??
              'Unable to load LUPA Risk Report clinics. Please try again.'
          );
          return;
        }
        setClinics(response.data.clinics);
        setOptionsError(null);
      })
      .catch(caught => {
        if (!cancelled) {
          logApiFailure('Failed to load LUPA Risk Report clinics', caught);
          setClinics([]);
          setOptionsError(
            getBusinessErrorMessage(caught) ??
              'Unable to load LUPA Risk Report clinics. Please try again.'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [asOfDate, hasPermission]);

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const download = async () => {
    try {
      const response = await exportLupaRiskReportReportsLupaRiskExportGet({
        query: {
          as_of_date: asOfDate || null,
          clinic_id: clinicId || null,
          q: q || null,
          sort_by: sortBy,
          sort_direction: sortDirection,
        },
        responseType: 'blob' as const,
      } as never);
      if (response.error || !response.data) {
        logApiFailure('Failed to export LUPA Risk Report', response);
        setError(
          await getUserFacingApiMessageAsync(
            normalizeReportError(response),
            'Unable to export LUPA Risk Report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(response as never, 'lupa-risk-report.xlsx');
    } catch (caught) {
      logApiFailure('Failed to export LUPA Risk Report', caught);
      setError(
        await getUserFacingApiMessageAsync(
          caught,
          'Unable to export LUPA Risk Report. Please try again.'
        )
      );
    }
  };

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='mr-1 h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void download()}
          disabled={loading || isSearchPending}
        >
          <Download className='h-4 w-4' /> Export
        </Button>
      </div>
      <div className='grid gap-3 rounded-lg border bg-card p-4 md:grid-cols-3'>
        <label
          className='space-y-1 text-sm font-medium'
          htmlFor='lupa-risk-as-of'
        >
          As of date
          <DatePicker
            id='lupa-risk-as-of'
            date={parseISOToLocalDate(asOfDate)}
            datePickerSize='default'
            onDateChange={date => setAsOfDate(date ?? null)}
            showClearButton
          />
        </label>
        <label
          className='space-y-1 text-sm font-medium'
          htmlFor='lupa-risk-clinic'
        >
          Clinic
          <Select
            value={clinicId || ALL_CLINICS}
            onValueChange={value =>
              setClinicId(value === ALL_CLINICS ? null : value)
            }
          >
            <SelectTrigger id='lupa-risk-clinic' selectSize='default'>
              <SelectValue placeholder='All clinics' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLINICS}>All clinics</SelectItem>
              {clinics.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className='space-y-1 text-sm font-medium'>
          Patient or MRN
          <Input
            value={searchInput}
            onChange={event => {
              setSearchInput(event.target.value);
              setSearchQuery(event.target.value);
            }}
            placeholder='Search patient or MRN'
          />
        </label>
      </div>
      <ReportTable
        data={report?.rows ?? []}
        columns={columns}
        getRowId={row => row.episode_id}
        caption='LUPA Risk Report results'
        sorting={sorting}
        onSortingChange={next =>
          setSorting(
            (next?.id ?? 'primary_difference') as LupaRiskSortBy,
            next?.direction ?? 'asc'
          )
        }
        isLoading={loading}
        emptyState={
          <EmptyState
            title={displayError ?? 'No matching active episodes'}
            description={
              displayError
                ? 'Please try again.'
                : 'Adjust filters and try again.'
            }
          />
        }
        pagination={{
          ...pagination,
          total: report?.total_count ?? 0,
          totalPages: Math.ceil((report?.total_count ?? 0) / pageSize),
        }}
        onPaginationChange={next => setPagination(next.page, next.pageSize)}
      />
    </div>
  );
};

export default LupaRiskReport;
